import React from 'react';
import { motion } from 'framer-motion';
import {
	ArrowUpRight,
	Circle,
	Command,
	Database,
	MessageSquare,
	Sparkles,
} from 'lucide-react';
import { SolutionConfig } from '../../data/solutions';
import { ParallaxImage } from '../common/ParallaxImage';

interface SolutionVisualProps {
	solution: SolutionConfig;
	compact?: boolean;
}

export const SolutionVisual: React.FC<SolutionVisualProps> = ({ solution, compact = false }) => {
	const Icon = solution.icon;
	const height = compact ? 'h-48' : 'h-[360px] md:h-[470px]';

	if (solution.image) {
		return (
			<div className={`relative overflow-hidden border border-white/[0.12] bg-[#0d0d0e] ${height}`}>
				<ParallaxImage
					src={solution.image}
					alt={`Interface visual de ${solution.name}`}
					intensity={compact ? 12 : 24}
					className="opacity-80 transition-opacity duration-700 group-hover:opacity-100"
				/>
				<div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-[#0b0b0c]/10 to-transparent" />
				<div className="absolute left-4 top-4 flex items-center gap-2 border border-white/15 bg-[#0b0b0c]/70 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60 backdrop-blur-md">
					<Circle className="h-2 w-2 fill-current" style={{ color: solution.theme.accent }} />
					{solution.name}
				</div>
				<div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4">
					<span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/60">Produto em foco</span>
					<span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: solution.theme.accent }}>
						Explorar <ArrowUpRight className="h-3 w-3" />
					</span>
				</div>
			</div>
		);
	}

	return (
		<div className={`relative overflow-hidden border border-white/[0.12] ${height}`} style={{ background: solution.theme.surface }}>
			<div
				className="absolute inset-0 opacity-40"
				style={{
					backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
					backgroundSize: compact ? '26px 26px' : '42px 42px',
				}}
			/>
			<div className="absolute -right-20 -top-20 h-48 w-48 rounded-full blur-3xl" style={{ background: solution.theme.accentSoft }} />
			<motion.div
				animate={{ y: [0, -8, 0], rotate: [0, 1.5, 0] }}
				transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
				className="absolute left-1/2 top-1/2 w-[72%] -translate-x-1/2 -translate-y-1/2 border border-white/20 bg-[#0b0b0c]/80 p-4 shadow-2xl backdrop-blur-xl md:p-6"
			>
				<div className="mb-6 flex items-center justify-between border-b border-white/10 pb-3 font-mono text-[9px] uppercase tracking-[0.13em] text-white/45">
					<span>{solution.slug} / workspace</span>
					<Icon className="h-3.5 w-3.5" style={{ color: solution.theme.accent }} />
				</div>
				<div className="mb-6 flex items-end gap-2" style={{ height: compact ? 64 : 116 }}>
					{[35, 54, 44, 74, 61, 86, 68].map((heightValue, index) => (
						<motion.span
							key={index}
							animate={{ height: [`${heightValue}%`, `${Math.max(24, heightValue - 18)}%`, `${heightValue}%`] }}
							transition={{ duration: 3 + index * 0.2, repeat: Infinity, ease: 'easeInOut' }}
							className="block flex-1"
							style={{ background: index === 5 ? solution.theme.accent : `${solution.theme.accent}66` }}
						/>
					))}
				</div>
				<div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-4 font-mono text-[9px] uppercase tracking-[0.1em] text-white/45">
					<span><strong className="block text-white">01</strong> fluxo</span>
					<span><strong className="block text-white">24/7</strong> ritmo</span>
					<span><strong className="block" style={{ color: solution.theme.accent }}>live</strong> estado</span>
				</div>
			</motion.div>
			<div className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
				<Database className="h-3 w-3" /> Visualização conceptual
			</div>
		</div>
	);
};

export const BotVisual: React.FC = () => (
	<div className="relative h-full min-h-72 overflow-hidden border border-white/[0.12] bg-[#101919] p-5">
		<div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(109,210,208,0.16),transparent_42%)]" />
		<div className="relative flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-[#6dd2d0]"><span>HiperBot / context</span><Sparkles className="h-3.5 w-3.5" /></div>
		<div className="relative mx-auto mt-12 max-w-xs space-y-3 font-mono text-xs">
			<div className="ml-auto w-fit border border-white/10 bg-white/[0.05] px-3 py-2 text-white/70">Como está a operação?</div>
			<div className="flex gap-2 border border-[#6dd2d0]/25 bg-[#6dd2d0]/10 px-3 py-3 text-[#bceeed]"><MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" /> A encontrar contexto para responder.</div>
		</div>
		<div className="absolute bottom-5 left-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-white/40"><Command className="h-3 w-3" /> Interface conceptual</div>
	</div>
);

export const VisualArrow: React.FC = () => <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />;
